import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { galleryItems } from "@/mocks/site";

const values = [
  {
    icon: "ri-quill-pen-line",
    title: "Story First",
    text: "Every frame exists to serve the story. We find the human truth before we ever roll camera.",
  },
  {
    icon: "ri-focus-3-line",
    title: "Craft Obsessed",
    text: "Cinematic lighting, intentional sound design, and color work that gives every film its own signature.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Real Partnership",
    text: "You work directly with the people making your film. No handoffs, no account managers.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Local Roots",
    text: "Born in Sonoma County, working with wineries, non-profits, and brands around the world.",
  },
];

const stats = [
  { value: "66★", label: "Five-star Google reviews" },
  { value: "12", label: "Years behind the lens" },
  { value: "200+", label: "Films delivered" },
  { value: "9", label: "Industry awards" },
];

export default function About() {
  return (
    <>
      <section className="bg-foreground-950 px-4 pb-20 pt-32 text-center md:px-10 md:pb-28 md:pt-40">
        <Reveal>
          <p className="font-label text-xs tracking-[0.25em] uppercase text-accent-400">
            About the Studio
          </p>
          <h1 className="mx-auto mt-5 max-w-4xl font-heading text-3xl font-extrabold uppercase leading-tight tracking-tight text-background-50 md:text-5xl">
            We make films that define brands.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-background-100/85 md:text-lg">
            Definition Films is an award-winning video production studio based in Sonoma
            County, California, creating brand films, commercials, and fundraising content
            for organizations that take their story seriously.
          </p>
        </Reveal>
      </section>

      <section className="bg-background-50 px-4 py-16 md:px-10 md:py-24">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
              <img
                src={galleryItems[5].image}
                alt={galleryItems[5].alt}
                title="Definition Films behind the scenes"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <p className="font-label text-xs tracking-[0.22em] uppercase text-accent-700">
              Our Story
            </p>
            <h2 className="mt-4 font-heading text-2xl tracking-tight text-foreground-950 md:text-3xl">
              A small team, deeply invested.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-foreground-600">
              We take on a limited number of projects each year. Not because we're exclusive,
              but because we're genuinely invested in every client we work with.
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              From the vineyards of Sonoma to national campaigns, we build long-term creative
              partnerships — the kind where your story gets better every year, not just on
              shoot day.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-block whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-xs tracking-[0.18em] uppercase text-background-50 transition-colors duration-200 hover:bg-primary-600"
            >
              Start a Project
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-16 md:px-10 md:py-24">
        <Reveal className="text-center">
          <h2 className="font-heading text-2xl tracking-tight text-foreground-950 md:text-4xl">
            WHAT DRIVES US
          </h2>
        </Reveal>
        <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value, index) => (
            <Reveal key={value.title} delay={index * 100}>
              <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${value.icon} text-xl`} />
                </div>
                <h3 className="mt-5 font-heading text-base text-foreground-950">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {value.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-background-50 px-4 py-16 md:px-10 md:py-24">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 80} className="text-center">
              <p className="font-heading text-4xl font-bold text-foreground-950 md:text-5xl">
                {stat.value}
              </p>
              <p className="mt-2 text-xs uppercase tracking-[0.15em] text-foreground-500">
                {stat.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-foreground-950 px-4 py-20 text-center md:px-10 md:py-24">
        <Reveal>
          <h2 className="mx-auto max-w-3xl font-heading text-3xl tracking-tight text-background-50 md:text-4xl">
            Let's build something people remember.
          </h2>
          <Link
            to="/contact"
            className="mt-8 inline-block whitespace-nowrap rounded-md bg-background-50 px-8 py-4 font-label text-xs tracking-[0.2em] uppercase text-foreground-950 transition-colors duration-200 hover:bg-accent-400 hover:text-accent-950"
          >
            Get in Touch
          </Link>
        </Reveal>
      </section>
    </>
  );
}